import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import SportsMmaIcon from '@mui/icons-material/SportsMma';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import agent from '../../agent';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';
import {
  awardEmoji, awardNameKey, compactNumber, contextNameKey, isMaxLevel, levelProgress, showsFreaksCard, THE_FREAKS_LINK,
} from '../../utils/theFreaks';
import useFreaksT from './useFreaksT';
import FreaksCharacterImage from './FreaksCharacterImage';
import { contextIcon } from './contextIcon';
import { barSx, FREAKS_DARK, FREAKS_GOLD } from './freaksStyles';

const Stat = ({ icon, label, value, color }) => (
  <Box sx={{ flex: 1, textAlign: 'center', minWidth: 0 }} aria-label={`${label}: ${value}`}>
    {React.cloneElement(icon, { sx: { fontSize: 18, color } })}
    <Typography noWrap sx={{ fontSize: 18, fontWeight: 800, color }}>{value}</Typography>
    <Typography noWrap sx={{ color: '#999', fontSize: 12 }}>{label}</Typography>
  </Box>
);

const cardSx = {
  display: 'block', width: '100%', textAlign: 'left', backgroundColor: FREAKS_DARK, borderRadius: 4.5, p: 2,
  color: 'white', border: '1px solid rgba(255,255,255,0.08)',
};

/** The Freaks card of a profile; hidden while loading, when it failed, or when the owner hid it. */
export const useFreaksProfile = (username, enabled = true) => {
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    if (!username || !enabled) return undefined;
    let active = true;
    agent.TheFreaks.profile(username).then((data) => active && setProfile(data)).catch(() => active && setProfile(null));
    return () => {
      active = false;
    };
  }, [username, enabled]);
  return profile;
};

// The Freaks in a profile's statistics: level with progress to the next one, season rank, power and record.
// On my own profile it opens the game (or invites me to start playing). Keep in sync with mobile FreaksProfileCard.
export function FreaksProfileCard({ profile }) {
  const { t } = useFreaksT();
  const navigate = useNavigate();
  const openGame = profile.isMe ? () => navigate(THE_FREAKS_LINK) : undefined;

  if (!profile.playing) {
    return (
      <ButtonBase
        onClick={openGame}
        disabled={!openGame}
        data-testid="freaks-profile-card"
        sx={{ ...cardSx, display: 'flex', alignItems: 'center', gap: 1.5 }}
      >
        <SportsMmaIcon sx={{ fontSize: 28, color: FREAKS_GOLD }}/>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 900, letterSpacing: 2 }}>{t('theFreaks.title').toUpperCase()}</Typography>
          <Typography sx={{ color: '#bbb', mt: 0.25 }}>{t('theFreaks.profile.notPlaying')}</Typography>
        </Box>
        <ChevronRightIcon sx={{ color: '#ccc' }}/>
      </ButtonBase>
    );
  }

  const progress = levelProgress(profile.respect, profile.levelRespect, profile.nextLevelRespect);
  const toNext = Math.max(0, (profile.nextLevelRespect ?? 0) - (profile.respect ?? 0));
  const record = profile.record ?? { missionsDone: 0, challengesWon: 0, challengesLost: 0 };
  const small = { color: '#999', fontSize: 12, mt: 0.75 };

  return (
    <ButtonBase component="div" onClick={openGame} disabled={!openGame} data-testid="freaks-profile-card" sx={cardSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {profile.character ? (
          <FreaksCharacterImage imageUrl={profile.character.imageUrl} emoji={profile.character.emoji} size={52}/>
        ) : null}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 900, letterSpacing: 2 }}>{t('theFreaks.title').toUpperCase()}</Typography>
          <Typography sx={{ color: FREAKS_GOLD, fontWeight: 700, mt: 0.25 }} data-testid="freaks-profile-level">
            {t('theFreaks.level', { level: profile.level })}
          </Typography>
          {profile.character ? (
            <Typography noWrap sx={{ fontSize: 12, color: '#bbb' }} data-testid="freaks-profile-character">{profile.character.name}</Typography>
          ) : null}
        </Box>
        {profile.context ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: '50%', px: 1.25, py: 0.75, borderRadius: 4, backgroundColor: 'rgba(255,255,255,0.08)' }}>
            {contextIcon(profile.context, { fontSize: 16, color: DEFAULT_THEME_COLOR })}
            <Typography noWrap sx={{ fontSize: 12 }}>{t(contextNameKey(profile.context))}</Typography>
          </Box>
        ) : null}
      </Box>

      <LinearProgress variant="determinate" value={progress * 100} sx={{ ...barSx(DEFAULT_THEME_COLOR), height: 8, borderRadius: 4, mt: 1.75 }}/>
      <Typography sx={small} data-testid="freaks-profile-next">
        {isMaxLevel(profile.level, profile.maxLevel)
          ? t('theFreaks.profile.maxLevel', { level: profile.level })
          : t('theFreaks.profile.toNextLevel', { respect: compactNumber(toNext), level: (profile.level ?? 1) + 1 })}
      </Typography>

      <Box sx={{ display: 'flex', mt: 1.5 }}>
        <Stat icon={<EmojiEventsIcon/>} label={t('theFreaks.profile.seasonRank')} value={profile.seasonRank ? `#${profile.seasonRank}` : '–'} color={FREAKS_GOLD}/>
        <Stat icon={<WorkspacePremiumIcon/>} label={t('theFreaks.respect')} value={compactNumber(profile.respect ?? 0)} color={DEFAULT_THEME_COLOR}/>
        <Stat icon={<FitnessCenterIcon/>} label={t('theFreaks.power')} value={compactNumber(profile.power ?? 0)} color="#fff"/>
      </Box>

      <Typography sx={small} data-testid="freaks-profile-record">
        {t('theFreaks.profile.record', { missions: record.missionsDone, won: record.challengesWon, lost: record.challengesLost })}
      </Typography>

      {profile.awards?.length ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mt: 1.25 }} data-testid="freaks-profile-awards">
          {profile.awards.slice(0, 8).map((award) => (
            <Box
              component="span"
              key={`${award.kind}-${award.key}-${award.season}`}
              title={t(awardNameKey(award.key))}
              aria-label={t(awardNameKey(award.key))}
              sx={{ fontSize: 20 }}
            >
              {awardEmoji(award.key)}
            </Box>
          ))}
          {profile.awards.length > 8 ? <Typography sx={{ ...small, mt: 0 }}>+{profile.awards.length - 8}</Typography> : null}
        </Box>
      ) : null}
    </ButtonBase>
  );
}

/** The card for a profile page: fetched for the username, shown only when showsFreaksCard says so. */
export default function FreaksProfileSection({ username, enabled }) {
  const profile = useFreaksProfile(username, enabled);
  if (!showsFreaksCard(profile)) return null;
  return (
    <Box sx={{ mx: 2, mb: 2 }}>
      <FreaksProfileCard profile={profile}/>
    </Box>
  );
}
